import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'
import { TabPanel, TabView } from 'primereact/tabview'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports
import loadingFilter from 'assets/icons/loading-filter.png'
import FilterService from 'service/FilterService'
import { PlanTabs } from 'types/PlanTabs'
import { IAbroad } from 'types/FloorType'
import { ICityList, IFirmList, IProperties } from 'types/FilterType'
import useDidMountEffect from 'utils/useDidMountEffect'
import Button1 from '../Button1'
import StarRating from '../Rating/Rating'
import AuthUtil from 'utils/AuthUtil'
import { IEmployeeSummary } from 'types/Employee'

interface IFilterTabProps {
  display: boolean
  setDisplay: Dispatch<SetStateAction<boolean>>
  floorSearchList: any[]
  name: string
  myFirm: boolean
  isAbroad: IAbroad
  filterList: (city: [], firm: [], location: [], block: [], floor: [], campus: [], rate: []) => void
  filterClear: () => void
  selectedCityList: any
  setSelectedCityList: Dispatch<SetStateAction<any>>
  selectedFirmList: any
  setSelectedFirmList: Dispatch<SetStateAction<any>>
  selectedLocationList: any
  setSelectedLocationList: Dispatch<SetStateAction<any>>
  selectedBlockList: any
  setSelectedBlockList: Dispatch<SetStateAction<any>>
  selectedFloorList: any
  setSelectedFloorList: Dispatch<SetStateAction<any>>
  selectedCampServiceList: any
  setSelectedCampServiceList: Dispatch<SetStateAction<any>>
  selectedRateList: any
  setSelectedRateList: Dispatch<SetStateAction<any>>
}

const RATE_LİST = [5, 4, 3, 2, 1]

export default function FilterTab({
  display,
  setDisplay,
  floorSearchList,
  name,
  myFirm,
  isAbroad,
  filterList,
  filterClear,
  selectedCityList,
  setSelectedCityList,
  selectedFirmList,
  setSelectedFirmList,
  selectedLocationList,
  setSelectedLocationList,
  selectedBlockList,
  setSelectedBlockList,
  selectedFloorList,
  setSelectedFloorList,
  selectedCampServiceList,
  setSelectedCampServiceList,
  selectedRateList,
  setSelectedRateList,
}: IFilterTabProps) {
  const { t } = useTranslation()
  const employee = AuthUtil.getEmployeeSummary() as IEmployeeSummary
  const PILOT_FIRMS = !!process.env.REACT_APP_PILOT_FIRMS
    ? process.env.REACT_APP_PILOT_FIRMS.split(',').map((str) => Number(str))
    : []
  const IS_PILOT_USER = !!PILOT_FIRMS?.length ? PILOT_FIRMS.includes(employee?.firmId || 0) : true

  const [activeIndex, setActiveIndex] = useState(0)
  const [authCityList, setAuthCityList] = useState<ICityList[]>([])
  const [firmList, setFirmList] = useState<IFirmList[]>([])
  const [locationProperties, setLocationProperties] = useState<IProperties[]>([])
  const [blockProperties, setBlockProperties] = useState<IProperties[]>([])
  const [floorProperties, setFloorProperties] = useState<IProperties[]>([])
  const [filterLoading, setFilterLoading] = useState<boolean>(false)

  useEffect(() => {
    setFilterLoading(true)
    if (activeIndex === 0) {
      FilterService.getAuthCityList(
        name === PlanTabs.COMMON
          ? {
              myFirm: myFirm,
              firmIdList: selectedFirmList.map((firmList: { id: any }) => firmList.id),
              isAbroad: isAbroad && isAbroad.isAbroad,
            }
          : {
              myFirm: myFirm,
              firmIdList: selectedFirmList.map((firmList: { id: any }) => firmList.id),
            }
      )
        .then((response) => {
          setAuthCityList(response.data)
          setFilterLoading(false)
        })
        .catch((e) => console.log(e))
    }
    if (activeIndex === 1) {
      FilterService.getFirmList(
        name === PlanTabs.COMMON
          ? {
              myFirm: myFirm,
              cityIdList: selectedCityList.map((cityList: { id: any }) => cityList.id),
              isAbroad: isAbroad && isAbroad.isAbroad,
            }
          : {
              myFirm: myFirm,
              cityIdList: selectedCityList.map((cityList: { id: any }) => cityList.id),
            }
      )
        .then((response) => {
          setFirmList(response.data)
          setFilterLoading(false)
        })
        .catch((e) => console.log(e))
    }
    if (activeIndex === 2) {
      FilterService.getAuthLocationProperties(myFirm)
        .then((response) => {
          setLocationProperties(response.data)
          setFilterLoading(false)
        })
        .catch((e) => console.log(e))
    }
    if (activeIndex === 3) {
      FilterService.getAuthBlockProperties(myFirm)
        .then((response) => {
          setBlockProperties(response.data)
          setFilterLoading(false)
        })
        .catch((e) => console.log(e))
    }
    if (activeIndex === 4) {
      FilterService.getAuthFloorProperties(myFirm)
        .then((response) => {
          setFloorProperties(response.data)
          setFilterLoading(false)
        })
        .catch((e) => console.log(e))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, isAbroad])

  const onCategoryChange = (e: { value: any; checked: boolean }, state: any, setState: React.Dispatch<any>) => {
    let _selectedCategories = [...state]
    if (e.checked) {
      _selectedCategories.push(e.value)
    } else {
      for (let i = 0; i < _selectedCategories.length; i++) {
        const selectedCategory = _selectedCategories[i]
        if (selectedCategory.id === e.value.id) {
          _selectedCategories.splice(i, 1)
          break
        }
      }
    }
    setState(_selectedCategories)
  }

  const filterSave = () => {
    filterList(
      selectedCityList.map((cityList: { id: any }) => cityList.id),
      selectedFirmList.map((firmList: { id: any }) => firmList.id),
      selectedLocationList.map((locationList: { id: any }) => locationList.id),
      selectedBlockList.map((blockList: { id: any }) => blockList.id),
      selectedFloorList.map((floorList: { id: any }) => floorList.id),
      selectedCampServiceList.map((campServiceList: { id: any }) => campServiceList.id),
      selectedRateList
    )
  }

  useDidMountEffect(() => {
    filterSave()
  }, [
    selectedCityList,
    selectedFirmList,
    selectedLocationList,
    selectedBlockList,
    selectedFloorList,
    selectedCampServiceList,
    selectedRateList,
  ])

  const onHide = (display: boolean) => {
    setDisplay(display)
    floorSearchList[0]?.items?.length === 0 && filterClear()
  }

  const CAMPUS_SERVICES = [
    { id: 1, name: t('campus-services.health-service') },
    { id: 2, name: t('campus-services.coiffeur') },
    { id: 3, name: t('campus-services.gym') },
    { id: 4, name: t('campus-services.lactation-room') },
    { id: 5, name: t('campus-services.parking-lot') },
    { id: 6, name: t('campus-services.meeting-room') },
    // { id: 7, name: t('campus-services.pool-vehicle') },
  ]

  const handleRateSelect = (rate: number) => {
    setSelectedRateList((prev: number[]) => {
      if (prev.includes(rate)) return prev.filter((r) => r !== rate)
      return [...prev, rate]
    })
  }

  const renderFooter = () => {
    return (
      <div className="row">
        <div className="col-6">
          <div className="col-3">
            <Button1 class={'button-blue'} label={t('buttons.clear')} onClick={() => filterClear()} />
          </div>
        </div>
        <div className="col-6">
          <Button1
            class={'button-blue'}
            label={t('buttons.apply') + ' ' + floorSearchList[0]?.items?.length + '+ ' + t('most-used-words.location')}
            disabled={floorSearchList[0]?.items?.length === 0}
            onClick={() => setDisplay(false)}
          />
        </div>
      </div>
    )
  }

  return (
    <Dialog
      header={t('input-texts.location-filtering')}
      visible={display}
      style={{ width: '50vw', height: '50vw' }}
      footer={renderFooter()}
      onHide={() => onHide(false)}>
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        {/* City */}
        <TabPanel
          header={
            t('most-used-words.city') + (selectedCityList.length > 0 ? ' (' + selectedCityList.length + ')' : '')
          }>
          {!filterLoading ? (
            <div className="row">
              {authCityList.map((city) => {
                return (
                  <div key={'city-' + city.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'city-' + city.id?.toString()}
                        name="category"
                        value={city}
                        onChange={(e) => onCategoryChange(e, selectedCityList, setSelectedCityList)}
                        checked={selectedCityList.some((item: { id: number }) => item.id === city.id)}
                      />
                      <label className="mx-1" htmlFor={'city-' + city.id?.toString()}>
                        {city.name}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'h-100'}>
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-full">
                <ProgressSpinner style={{ width: '120px' }} />
                <img alt="filterLoading" style={{ position: 'absolute', width: '40px' }} src={loadingFilter} />
              </div>
            </div>
          )}
        </TabPanel>
        {/* Firm */}
        <TabPanel
          header={
            t('most-used-words.firm') + (selectedFirmList.length > 0 ? ' (' + selectedFirmList.length + ')' : '')
          }>
          {!filterLoading ? (
            <div className="row">
              {firmList.map((firm) => {
                return (
                  <div key={'firm-' + firm.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'firm-' + firm.id?.toString()}
                        name="category"
                        value={firm}
                        onChange={(e) => onCategoryChange(e, selectedFirmList, setSelectedFirmList)}
                        checked={selectedFirmList.some((item: { id: number }) => item.id === firm.id)}
                      />
                      <label className="mx-1" htmlFor={'firm-' + firm.id?.toString()}>
                        {firm.name}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'h-100'}>
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-full">
                <ProgressSpinner style={{ width: '120px' }} />
                <img alt="filterLoading" style={{ position: 'absolute', width: '40px' }} src={loadingFilter} />
              </div>
            </div>
          )}
        </TabPanel>
        {/* Location */}
        <TabPanel
          header={
            t('most-used-words.location') +
            (selectedLocationList.length > 0 ? ' (' + selectedLocationList.length + ')' : '')
          }>
          {!filterLoading ? (
            <div className="row">
              {locationProperties.map((location) => {
                return (
                  <div key={'location-' + location.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'location-' + location.id?.toString()}
                        name="category"
                        value={location}
                        className={location.isActive ? '' : 'pe-none'}
                        onChange={(e) => onCategoryChange(e, selectedLocationList, setSelectedLocationList)}
                        checked={selectedLocationList.some((item: { id: number }) => item.id === location.id)}
                        disabled={!location.isActive}
                      />
                      <label
                        className={location.isActive ? 'mx-1' : 'disable-properties mx-1'}
                        htmlFor={'location-' + location.id?.toString()}>
                        {t(`auth-properties.${location.name}`)}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'h-100'}>
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-full">
                <ProgressSpinner style={{ width: '120px' }} />
                <img alt="filterLoading" style={{ position: 'absolute', width: '40px' }} src={loadingFilter} />
              </div>
            </div>
          )}
        </TabPanel>
        {/* Block */}
        <TabPanel
          header={
            t('most-used-words.block') + (selectedBlockList.length > 0 ? ' (' + selectedBlockList.length + ')' : '')
          }>
          {!filterLoading ? (
            <div className="row">
              {blockProperties.map((block) => {
                return (
                  <div key={'block-' + block.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'block-' + block.id?.toString()}
                        name="category"
                        value={block}
                        className={block.isActive ? '' : 'pe-none'}
                        onChange={(e) => onCategoryChange(e, selectedBlockList, setSelectedBlockList)}
                        checked={selectedBlockList.some((item: { id: number }) => item.id === block.id)}
                        disabled={!block.isActive}
                      />
                      <label
                        className={block.isActive ? 'mx-1' : 'disable-properties mx-1'}
                        htmlFor={'block-' + block.id?.toString()}>
                        {t(`auth-properties.${block.name}`)}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'h-100'}>
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-full">
                <ProgressSpinner style={{ width: '120px' }} />
                <img alt="filterLoading" style={{ position: 'absolute', width: '40px' }} src={loadingFilter} />
              </div>
            </div>
          )}
        </TabPanel>
        {/* Floor */}
        <TabPanel
          header={
            t('most-used-words.floor') + (selectedFloorList.length > 0 ? ' (' + selectedFloorList.length + ')' : '')
          }>
          {!filterLoading ? (
            <div className="row">
              {floorProperties.map((floor) => {
                return (
                  <div key={'floor-' + floor.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'block-' + floor.id?.toString()}
                        name="category"
                        className={floor.isActive ? '' : 'pe-none'}
                        value={floor}
                        onChange={(e) => onCategoryChange(e, selectedFloorList, setSelectedFloorList)}
                        checked={selectedFloorList.some((item: { id: number }) => item.id === floor.id)}
                        disabled={!floor.isActive}
                      />
                      <label
                        className={floor.isActive ? 'mx-1' : 'disable-properties mx-1'}
                        htmlFor={'block-' + floor.id?.toString()}>
                        {t(`auth-properties.${floor.name}`)}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={'h-100'}>
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-full">
                <ProgressSpinner style={{ width: '120px' }} />
                <img alt="filterLoading" style={{ position: 'absolute', width: '40px' }} src={loadingFilter} />
              </div>
            </div>
          )}
        </TabPanel>
        {/* Campus Services */}
        {name === PlanTabs.COMMON && (
          <TabPanel
            header={
              t('tabs.on-campus-services') +
              (selectedCampServiceList.length > 0 ? ' (' + selectedCampServiceList.length + ')' : '')
            }>
            <div className="row">
              {CAMPUS_SERVICES.map((campus) => {
                return (
                  <div key={'campus-' + campus.id} className={'col-4'}>
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId={'campus-' + campus.id.toString()}
                        name="category"
                        value={campus}
                        onChange={(e) => onCategoryChange(e, selectedCampServiceList, setSelectedCampServiceList)}
                        checked={selectedCampServiceList.some((item: { id: number }) => item.id === campus.id)}
                      />
                      <label role="button" className="mx-1" htmlFor={'campus-' + campus.id.toString()}>
                        {t(`${campus.name}`)}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          </TabPanel>
        )}
        {/* Rating */}
        {name === PlanTabs.COMMON && IS_PILOT_USER && AuthUtil.isAdmin(employee?.authList) && (
          <TabPanel
            header={
              t('homepage.evaluation-point') + (selectedRateList.length > 0 ? ' (' + selectedRateList.length + ')' : '')
            }>
            <div className="row d-flex flex-column gap-3 justify-content-center">
              {RATE_LİST.map((r) => (
                <RatingBtn
                  key={'rate-' + r}
                  selected={selectedRateList?.includes(r)}
                  onClick={() => handleRateSelect(r)}>
                  <StarRating readOnly value={r} />
                </RatingBtn>
              ))}
            </div>
          </TabPanel>
        )}
      </TabView>
    </Dialog>
  )
}

const RatingBtn = styled.button`
  outline: none;
  display: flex;
  background-color: ${({ selected }: { selected?: boolean }) => (selected ? 'rgba(88, 104, 184, 0.3)' : 'transparent')};
  width: 165px;
  padding: 10px 16px;
  border-radius: 5px;
  border: 1px solid #5868b8;
`
